"use client";

import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import styles from "@/styles/product-detail/addon-tabbar.module.scss";

interface TabItem {
  id: number | string;
  name: string;
  itemWidth?: string;
  reactNode?: ReactNode;
}

interface Props {
  activeTab: number;
  tabList: TabItem[];
  tabItemRightMargin?: string;
  onTabChange: (index: number) => void;
}

export default function AddOnTabBar({
  activeTab,
  tabList,
  tabItemRightMargin,
  onTabChange,
}: Props) {
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const listRef = useRef<HTMLDivElement>(null);
  const [indicatorLeft, setIndicatorLeft] = useState(0);

  const updateIndicator = useCallback(() => {
    const activeTabEl = tabRefs.current[activeTab];
    const listEl = listRef.current;
    if (!activeTabEl || !listEl) return;

    const tabRect = activeTabEl.getBoundingClientRect();
    const listRect = listEl.getBoundingClientRect();
    setIndicatorLeft(tabRect.left - listRect.left + tabRect.width / 2 - 7);
  }, [activeTab]);

  useEffect(() => {
    updateIndicator();
    window.addEventListener("resize", updateIndicator);
    return () => window.removeEventListener("resize", updateIndicator);
  }, [updateIndicator, tabList]);

  return (
    <div className={styles["tab-box"]}>
      <div className={styles["tab-list"]} ref={listRef}>
        {tabList.map((item, index) => (
          <div
            key={item.id}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            className={`${styles["tab-item"]} ${
              index === activeTab ? styles["active"] : ""
            }`}
            style={{
              width: item.itemWidth || "auto",
              marginRight: tabItemRightMargin || "24px",
            }}
            onClick={() => onTabChange(index)}
          >
            {item.reactNode || (
              <div className={styles["tab-name"]}>{item.name}</div>
            )}
          </div>
        ))}

        {activeTab > -1 && (
          <div
            className={styles["tab-indicator"]}
            style={{ left: `${indicatorLeft}px` }}
          />
        )}
      </div>
    </div>
  );
}
